import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme";
import type { ThemeMode } from "./tokens";

const STORAGE_KEY = "sirius-theme-mode";

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used within a ThemeModeProvider");
  return ctx;
};

const readStoredMode = (): ThemeMode | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // storage unavailable (private mode / non-browser) — fall back to system preference
    return null;
  }
};

const persistMode = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // storage unavailable — mode stays in memory for this session only
  }
};

const resolveInitialMode = (defaultMode?: ThemeMode): ThemeMode => {
  if (defaultMode) return defaultMode;
  // ponytail: do NOT auto-adopt `prefers-color-scheme: dark`. The MUI dark palette
  // exists, but the app's legacy Sirius SCSS chrome (sidebars, pages, modals) is
  // light-only, so a system-dark machine renders a broken split — dark MUI surfaces
  // + faint dark-mode text on light legacy pages. Stay light until the legacy chrome
  // is dark-ready; re-add the prefers-color-scheme branch then. Explicit user/stored
  // choice is still honored so dark can be tested.
  return readStoredMode() ?? "light";
};

/** Current preferred mode (persisted → system → light). For imperative renders
 *  (e.g. confirmDialog) that mount outside the provider tree and still need the theme. */
export const getStoredThemeMode = (): ThemeMode => resolveInitialMode();

export interface ThemeModeProviderProps {
  children: ReactNode;
  /** Force a mode (overrides stored + system preference). Mainly for tests. */
  defaultMode?: ThemeMode;
}

/**
 * Single source of theme mode for both apps. Provides the MUI theme for the
 * current mode and a `useThemeMode()` hook to read/toggle it. Defaults from
 * a persisted choice, then `prefers-color-scheme`, then light. Replaces
 * diligence's ad-hoc DisplayThemeContext.
 */
export const ThemeModeProvider = ({ children, defaultMode }: ThemeModeProviderProps) => {
  const [mode, setModeState] = useState<ThemeMode>(() => resolveInitialMode(defaultMode));

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    persistMode(m);
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark";
      persistMode(next);
      return next;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
